import { io, Socket } from "socket.io-client";

// ============ TIPOS ============
interface Position {
  deviceId: string;
  device_id?: string;
  company_id?: string;
  vehicle_id?: string;
  remolque_id?: string | null;
  imei?: string;
  brand_name?: string | null;
  brand_model?: string | null;
  latitude: number;
  longitude: number;
  speed?: number;
  course?: number;
  altitude?: number;
  accuracy?: number;
  timestamp: string;
  address?: string;
  point?: Record<string, unknown> & {
    brand_name?: string | null;
    brand_model?: string | null;
  };
}

interface DeviceEvent {
  deviceId: string;
  type: string;
  timestamp: string;
  data?: Record<string, unknown>;
}

interface AddressResponse {
  deviceId: string;
  address: string;
  requestId?: string;
}

interface CommandStatusResponse {
  device_id: string;
  company_id?: string | null;
  vehicle_id?: string | null;
  remolque_id?: string | null;
  command: {
    request_id: string;
    imei?: string;
    brand_id?: string;
    brand?: string;
    model?: string;
    command_doc_id?: string;
    command_code: string;
    command_name?: string;
    password_required?: boolean;
    data?: boolean;
    response_expected?: boolean;
    transport?: {
      mode: string;
      value: string;
      channel: string;
    };
    status: string;
    edge_id?: string;
    edge_type?: string;
    protocol_family?: string;
    raw_request?: Record<string, unknown> | null;
    raw_response?: Record<string, unknown> | null;
    sent_at?: number | null;
    finished_at?: number | null;
    error?: string | null;
  };
}

interface LastPositionRequest {
  deviceId: string;
  withAddress?: boolean;
  requestId?: string;
}

interface SubscribePayload {
  deviceIds: string[];
}

interface SubscribedResponse {
  deviceIds: string[];
  success: boolean;
}

export interface TripTrackingEvent {
  device_id: string;
  trip_id: string | null;
  trip_title: string | null;
  route_id: string;
  route_name: string;
  company_id: string;
  event_type:
    | "VIAJE_INICIADO"
    | "DEMORA_EN_INICIO"
    | "INICIO_TARDIO"
    | "DESVIO_DE_RUTA"
    | "SIGUE_FUERA_DE_RUTA"
    | "REGRESO_A_RUTA"
    | "VIAJE_COMPLETADO";
  trip_status: "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED";
  is_deviated: boolean;
  message: string;
  device_name: string | null;
  vehicle_name: string | null;
  timestamp: number;
  coordinates: { lat: number; lon: number };
  distance_meters?: number;
  duration_minutes?: number;
  trip_duration_seconds?: number;
  trip_duration_minutes?: number;
}

export interface TripStatusResponse {
  deviceId: string;
  states: Array<{
    route_id: string;
    status: "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED";
    trip_id: string | null;
    started_at: number | null;
    completed_at: number | null;
    assignment_key: string | null;
    route_name: string | null;
    trip_title: string | null;
    is_deviated: boolean;
  }> | null;
  requestId?: string;
}

interface TripStatusRequest {
  deviceId: string;
  requestId?: string;
}

export const CENTINELA_EVENT_TYPES = [
  "unidad.estado",
  "caso.abierto",
  "caso.actualizado",
  "caso.cerrado",
  "llamada.iniciada",
  "llamada.contestada",
  "llamada.terminada",
  "ubicacion.recibida",
  "consumo.aviso",
] as const;

export type CentinelaEventType = (typeof CENTINELA_EVENT_TYPES)[number];

export interface CentinelaSocketEvent {
  tipo: CentinelaEventType;
  company_id: string;
  device_id: string | null;
  case_id: string | null;
  data: Record<string, unknown>;
  at: string;
}

interface CentinelaSubscribePayload {
  companyIds: string[];
}

interface CentinelaSubscribedResponse {
  companyIds: string[];
}

interface ServerToClientEvents {
  subscribed: (response: SubscribedResponse) => void;
  centinela_subscribed: (response: CentinelaSubscribedResponse) => void;
  centinela_event: (event: CentinelaSocketEvent) => void;
  position: (position: Position) => void;
  event: (event: DeviceEvent) => void;
  address: (address: AddressResponse) => void;
  last_position: (position: Position) => void;
  command_status: (response: CommandStatusResponse) => void;
  trip_tracking: (event: TripTrackingEvent) => void;
  trip_status: (response: TripStatusResponse) => void;
}

interface ClientToServerEvents {
  subscribe: (payload: SubscribePayload) => void;
  "subscribe:centinela": (payload: CentinelaSubscribePayload) => void;
  "get:last_position": (request: LastPositionRequest) => void;
  "get:trip_status": (request: TripStatusRequest) => void;
}

// ============ SOCKET ============
const SOCKET_URL = import.meta.env.VITE_DUMAX_SOCKET_URL || "http://localhost:3000";
//const SOCKET_URL = "http://localhost:8082";

let socket: Socket<ServerToClientEvents, ClientToServerEvents> | null = null;

export const getSocket = (): Socket<ServerToClientEvents, ClientToServerEvents> => {
  if (!socket) {
    socket = io(SOCKET_URL, {
      transports: ["websocket"],
      autoConnect: false,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    });
  }
  return socket;
};

export const connectSocket = (): Socket<ServerToClientEvents, ClientToServerEvents> => {
  const socketInstance = getSocket();
  if (!socketInstance.connected) {
    socketInstance.connect();
  }
  return socketInstance;
};

export const disconnectSocket = (): void => {
  if (socket?.connected) {
    socket.disconnect();
  }
};

export const isSocketConnected = (): boolean => {
  return socket?.connected ?? false;
};

export const subscribeToDevices = (deviceIds: string[]): void => {
  const socketInstance = getSocket();
  socketInstance.emit("subscribe", { deviceIds });
};

export const subscribeToCentinela = (companyIds: string[]): void => {
  if (companyIds.length === 0) return;
  const socketInstance = getSocket();
  socketInstance.emit("subscribe:centinela", { companyIds });
};

export const getLastPosition = (
  deviceId: string,
  withAddress = true,
  requestId?: string
): void => {
  const socketInstance = getSocket();

  if (!socketInstance.connected) {
    console.warn("⚠️ [Socket] getLastPosition - Socket no conectado, no se puede emitir");
    return;
  }

  socketInstance.emit("get:last_position", {
    deviceId,
    withAddress,
    requestId,
  });
};

// Solicitar last_position para múltiples devices en chunks para no saturar el socket
const BATCH_CHUNK_SIZE = 25;
const BATCH_CHUNK_DELAY = 50; // ms entre chunks

/*
  Lo que se pidió antes de que el socket estuviera listo.

  Esta función se llama en el arranque, dentro de un `setTimeout`, y si el
  socket todavía no había conectado se limitaba a avisar por consola y volver:
  la petición se perdía y NADIE la reintentaba, así que esas unidades se
  quedaban sin su última posición hasta que otra cosa disparara una recarga.
  Había reintento sólo en la reconexión explícita, que es otro camino.

  Ahora se guarda y se suelta en cuanto conecta. Es un Set para que pedir dos
  veces el mismo equipo no lo pida dos veces.
*/
const pendientesDePosicion = new Set<string>();
let esperandoConexion = false;

const soltarPendientes = () => {
  if (pendientesDePosicion.size === 0) return;
  const ids = [...pendientesDePosicion];
  pendientesDePosicion.clear();
  getLastPositionBatch(ids, false);
};

export const getLastPositionBatch = (
  deviceIds: string[],
  withAddress = false
): void => {
  const socketInstance = getSocket();

  if (!socketInstance.connected) {
    deviceIds.forEach((id) => pendientesDePosicion.add(id));
    if (!esperandoConexion) {
      esperandoConexion = true;
      socketInstance.once("connect", () => {
        esperandoConexion = false;
        soltarPendientes();
      });
    }
    return;
  }

  for (let i = 0; i < deviceIds.length; i += BATCH_CHUNK_SIZE) {
    const chunk = deviceIds.slice(i, i + BATCH_CHUNK_SIZE);
    const delay = (i / BATCH_CHUNK_SIZE) * BATCH_CHUNK_DELAY;

    if (delay === 0) {
      chunk.forEach((deviceId) => {
        socketInstance.emit("get:last_position", { deviceId, withAddress, requestId: deviceId });
      });
    } else {
      setTimeout(() => {
        if (!socketInstance.connected) return;
        chunk.forEach((deviceId) => {
          socketInstance.emit("get:last_position", { deviceId, withAddress, requestId: deviceId });
        });
      }, delay);
    }
  }
};

export const getTripStatus = (deviceId: string, requestId?: string): void => {
  const socketInstance = getSocket();
  if (!socketInstance.connected) return;
  socketInstance.emit("get:trip_status", { deviceId, requestId: requestId || deviceId });
};